<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useSessionStore } from "../stores/session";
import { api } from "../api";
import {
  Building2,
  GraduationCap,
  BadgeCheck,
  Router,
  ArrowRightLeft,
  CheckCircle2,
  XCircle,
  ScanLine,
} from "@lucide/vue";
import PageHeader from "../components/PageHeader.vue";
import UiSelect from "../ui/UiSelect.vue";
import UiToast from "../ui/UiToast.vue";
import UiStudentAvatar from "../ui/UiStudentAvatar.vue";

const session = useSessionStore();
const loading = ref(true);
const stats = ref({});
const logs = ref([]);
const devices = ref([]);
const selectedDevice = ref("");
const uid = ref("");
const toast = ref("");
const toastType = ref("success");
const scope = computed(() =>
  session.selectedSchoolId ? `?schoolId=${session.selectedSchoolId}` : "",
);
const deviceOptions = computed(() =>
  devices.value.map((device) => ({
    value: String(device.id),
    label: device.name,
    description: device.location || device.school_name || "Turnike cihazı",
  })),
);
const cards = computed(() => [
  { label: "Aktif okul", value: stats.value.schools || 0, icon: Building2 },
  {
    label: "Aktif öğrenci",
    value: stats.value.students || 0,
    icon: GraduationCap,
  },
  { label: "Aktif kart", value: stats.value.cards || 0, icon: BadgeCheck },
  { label: "Aktif cihaz", value: stats.value.devices || 0, icon: Router },
  {
    label: "Bugünkü geçiş",
    value: stats.value.todayTotal || 0,
    icon: ArrowRightLeft,
  },
  {
    label: "İzin verilen",
    value: stats.value.todayAllowed || 0,
    icon: CheckCircle2,
  },
  { label: "Reddedilen", value: stats.value.todayDenied || 0, icon: XCircle },
]);
function show(message, type = "success") {
  toast.value = message;
  toastType.value = type;
  setTimeout(() => (toast.value = ""), 3500);
}
async function load() {
  loading.value = true;
  try {
    const [summary, deviceResult] = await Promise.all([
      api(`/api/summary${scope.value}`),
      session.isParent
        ? Promise.resolve({ devices: [] })
        : api(`/api/devices${scope.value}`),
    ]);
    stats.value = summary.stats;
    logs.value = summary.recentLogs;
    devices.value = deviceResult.devices;
    if (!selectedDevice.value && devices.value[0])
      selectedDevice.value = String(devices.value[0].id);
  } catch (err) {
    show(err.message, "error");
  } finally {
    loading.value = false;
  }
}
async function testCard() {
  const device = devices.value.find(
    (item) => String(item.id) === selectedDevice.value,
  );
  if (!device || !uid.value) return;
  try {
    const result = await api("/api/device/access", {
      method: "POST",
      body: JSON.stringify({ deviceKey: device.device_key, uid: uid.value }),
    });
    show(
      result.allowed
        ? `${result.message}: ${result.direction === "entry" ? "giriş" : "çıkış"}`
        : result.message,
      result.allowed ? "success" : "error",
    );
    uid.value = "";
    await load();
  } catch (err) {
    show(err.message, "error");
  }
}
const onRefresh = () => load();
onMounted(() => {
  load();
  window.addEventListener("lopiy:refresh", onRefresh);
});
onBeforeUnmount(() => window.removeEventListener("lopiy:refresh", onRefresh));
const dateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("tr-TR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value))
    : "-";
</script>
<template>
  <PageHeader
    title="Operasyon merkezi"
    description="Erva Spor Okullarının öğrenci, cihaz ve geçiş hareketlerinin canlı özeti."
  />
  <div v-if="loading" class="grid min-h-80 place-items-center">
    <div class="loader-ring"></div>
  </div>
  <template v-else>
    <section class="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <article v-for="card in cards" :key="card.label" class="stat-card group">
        <div class="mb-5 flex items-center justify-between">
          <span class="text-sm font-semibold text-slate-500">{{
            card.label
          }}</span
          ><span
            class="grid size-9 place-items-center rounded-md bg-brand-50 text-brand-600"
            ><component :is="card.icon" class="size-[18px]"
          /></span>
        </div>
        <strong class="text-3xl font-extrabold text-ink-950">{{
          card.value.toLocaleString("tr-TR")
        }}</strong>
        <div
          class="mt-3 h-1 w-10 rounded-full bg-brand-500 transition-all group-hover:w-16"
        ></div>
      </article>
    </section>
    <section class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div class="panel overflow-hidden">
        <div class="toolbar">
          <div>
            <h3 class="font-extrabold text-ink-950">Son geçişler</h3>
            <p class="mt-0.5 text-xs text-slate-500">
              En güncel 12 cihaz hareketi
            </p>
          </div>
          <RouterLink to="/loglar" class="btn-secondary h-9"
            >Tümünü gör</RouterLink
          >
        </div>
        <div class="overflow-x-auto">
          <table class="data-table">
            <thead>
              <tr>
                <th>Zaman</th>
                <th>Öğrenci</th>
                <th>Geçiş okulu</th>
                <th>Cihaz</th>
                <th>Yön</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td class="whitespace-nowrap">
                  {{ dateTime(log.created_at) }}
                </td>
                <td>
                  <div class="flex min-w-40 items-center gap-3">
                    <UiStudentAvatar
                      :src="log.photo_path"
                      :first-name="log.first_name"
                      :last-name="log.last_name"
                      size="sm"
                    />
                    <span class="font-semibold text-ink-900">{{
                      [log.first_name, log.last_name]
                        .filter(Boolean)
                        .join(" ") || "Bilinmeyen kart"
                    }}</span>
                  </div>
                </td>
                <td>{{ log.school_name || "-" }}</td>
                <td>{{ log.device_name || "-" }}</td>
                <td>{{ log.direction === "exit" ? "Çıkış" : "Giriş" }}</td>
                <td>
                  <span
                    :class="
                      log.decision === 'allowed'
                        ? 'chip-success'
                        : 'chip-danger'
                    "
                    >{{ log.decision === "allowed" ? "İzinli" : "Red" }}</span
                  >
                </td>
              </tr>
              <tr v-if="!logs.length">
                <td colspan="6" class="py-12 text-center text-slate-400">
                  Henüz geçiş kaydı yok.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <aside v-if="!session.isParent" class="panel h-fit p-5">
        <div class="mb-5 flex items-center gap-3">
          <span
            class="grid size-10 place-items-center rounded-md bg-brand-50 text-brand-600"
            ><ScanLine class="size-5"
          /></span>
          <div>
            <h3 class="font-extrabold text-ink-950">Kart testi</h3>
            <p class="text-xs text-slate-500">Canlı cihaz doğrulaması</p>
          </div>
        </div>
        <form class="space-y-4" @submit.prevent="testCard">
          <label class="field"
            ><span class="field-label">Cihaz</span
            ><UiSelect
              v-model="selectedDevice"
              :options="deviceOptions"
              searchable
              search-placeholder="Cihaz ara..." /></label
          ><label class="field"
            ><span class="field-label">Kart UID</span
            ><input
              v-model="uid"
              class="input font-mono uppercase"
              placeholder="Örn. 59A4E306" /></label
          ><button class="btn-primary w-full">
            <ScanLine class="size-4" />Test Et
          </button>
        </form>
        <p
          class="mt-4 rounded-md bg-slate-50 p-3 text-xs leading-5 text-slate-500"
        >
          Bilinmeyen kart okutulduğunda UID bu alanda kullanılabilir.
        </p>
      </aside>
    </section>
  </template>
  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
